import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, LogOut, MessageCircle, Shield, LayoutDashboard } from "lucide-react";
import Logo from "./Logo";
import { useApp } from "../context/AppContext";
import { scrollToId, scrollToTop } from "../lib/scroll";
import { waGeneralLink } from "../lib/api";

const LINKS = [
    { id: "beranda", label: "Beranda", tid: "nav-home-link" },
    { id: "misi", label: "Misi", tid: "nav-mission-link" },
    { id: "cara-kerja", label: "Cara Kerja", tid: "nav-howitworks-link" },
    { id: "katalog", label: "Katalog", tid: "nav-catalog-link" },
    { id: "koleksi", label: "Koleksi Kurasi", tid: "nav-collections-link" },
];

export default function Navbar() {
    const { user, logout } = useApp();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const go = (id) => {
        setOpen(false);
        if (window.location.pathname !== "/") {
            navigate("/");
            setTimeout(() => scrollToId(id), 500);
        } else {
            scrollToId(id);
        }
    };

    return (
        <header className="fixed top-0 inset-x-0 z-50 bg-brand-forest border-b-2 border-brand-ink">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 md:h-[72px]">
                    <button data-testid="header-logo" onClick={scrollToTop} className="active:scale-95 transition-transform" aria-label="Acture.id — ke atas">
                        <Logo dark />
                    </button>

                    <nav className="hidden md:flex items-center gap-1">
                        {LINKS.map((l) => (
                            <button
                                key={l.id}
                                data-testid={l.tid}
                                onClick={() => go(l.id)}
                                className="px-4 py-2 rounded-full text-sm font-bold text-brand-cream/85 hover:bg-brand-sunshine/25 hover:text-brand-cream transition-colors"
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>

                    <div className="hidden md:flex items-center gap-2.5">
                        <a
                            href={waGeneralLink()}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="nav-wa-button"
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border-2 border-brand-ink bg-brand-cream text-brand-forest font-bold text-sm shadow-hard-sm hover:bg-brand-wa hover:text-white transition-colors"
                        >
                            <MessageCircle className="w-4 h-4" strokeWidth={2.6} /> Chat CS
                        </a>
                        {user && (
                            <div className="flex items-center gap-2">
                                <button
                                    data-testid="nav-dashboard-button"
                                    onClick={() => navigate("/acture-admin")}
                                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border-2 border-brand-ink bg-brand-sunshine text-brand-ink font-bold text-sm shadow-hard-sm hover:-translate-y-0.5 hover:shadow-hard transition-all active:translate-y-0"
                                >
                                    <LayoutDashboard className="w-4 h-4" strokeWidth={2.6} /> Dashboard
                                </button>
                                <button
                                    data-testid="nav-logout-button"
                                    onClick={logout}
                                    className="p-2.5 rounded-full border-2 border-brand-cream/50 text-brand-cream hover:bg-brand-tomato hover:text-brand-cream hover:border-brand-ink transition-colors"
                                    aria-label="Logout"
                                >
                                    <LogOut className="w-4 h-4" strokeWidth={2.6} />
                                </button>
                            </div>
                        )}
                    </div>

                    <button
                        data-testid="nav-mobile-toggle"
                        className="md:hidden p-2.5 rounded-full border-2 border-brand-cream/60 text-brand-cream"
                        onClick={() => setOpen((o) => !o)}
                        aria-label="Menu"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="md:hidden overflow-hidden border-t-2 border-brand-ink bg-brand-forest"
                    >
                        <div className="px-4 py-4 flex flex-col gap-2">
                            {LINKS.map((l) => (
                                <button key={l.id} onClick={() => go(l.id)} className="text-left px-4 py-3 rounded-2xl font-bold text-brand-cream hover:bg-brand-sunshine/25 transition-colors">
                                    {l.label}
                                </button>
                            ))}
                            <div className="flex gap-2 pt-2">
                                <a
                                    href={waGeneralLink()}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex-1 px-4 py-3 rounded-2xl border-2 border-brand-ink bg-brand-cream text-brand-forest font-bold text-center shadow-hard-sm"
                                >
                                    Chat CS via WhatsApp
                                </a>
                                {user && (
                                    <button
                                        data-testid="nav-logout-button"
                                        onClick={() => { setOpen(false); logout(); }}
                                        className="px-4 py-3 rounded-2xl border-2 border-brand-cream/50 text-brand-cream font-bold"
                                    >
                                        Logout
                                    </button>
                                )}
                            </div>
                            {user && (
                                <Link to="/acture-admin" onClick={() => setOpen(false)} className="text-center px-4 py-3 rounded-2xl border-2 border-brand-ink bg-brand-sunshine text-brand-ink font-bold shadow-hard-sm">
                                    Buka Dashboard Admin
                                </Link>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
